/**
 * Completeness Card - Dashboard widget showing fleet data completeness
 *
 * Shows a progress indicator and checklist of what's configured vs. what's missing.
 * Non-blocking - users can dismiss or click through to complete items.
 */

import React, { useState, useEffect } from 'react';
import {
  CheckCircle2, Circle, ChevronRight, Target, AlertTriangle,
  Building2, Truck, Users, Shield, MapPin, FileText, Wrench, ClipboardCheck
} from 'lucide-react';
import { onboardingAPI } from '../../../api.js';

const SECTION_ICONS = {
  company: Building2,
  vehicles: Truck,
  drivers: Users,
  assignments: Truck,
  insurance: Shield,
  licenses: FileText,
  sites: MapPin,
  maintenance: Wrench,
  inspections: ClipboardCheck,
  documents: FileText,
};

export function CompletenessCard({ onNavigate, compact = false }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    loadCompleteness();
  }, []);

  const loadCompleteness = async () => {
    try {
      setLoading(true);
      const response = await onboardingAPI.getCompleteness();
      if (response.ok) {
        setMetrics(response.data);
      } else {
        setError('Erreur de chargement');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Don't render if dismissed or fully complete
  if (dismissed || (metrics?.overall >= 100)) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 animate-pulse shadow-sm">
        <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/3 mb-4"></div>
        <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded w-full mb-2"></div>
        <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded w-2/3"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return null; // Silently fail - this is a non-critical widget
  }

  const { overall, sections = [] } = metrics || {};
  const completedCount = sections.filter(s => s.complete).length;
  const totalCount = sections.length;

  // Get incomplete sections for quick actions
  const incompleteSections = sections.filter(s => !s.complete).slice(0, 3);

  // Progress bar color based on completion
  const progressColor = overall >= 80 ? 'from-green-500 to-green-400' :
                       overall >= 50 ? 'from-amber-500 to-amber-400' :
                       'from-red-500 to-red-400';

  // Compact version for sidebar or small spaces
  if (compact) {
    return (
      <div className="bg-white dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-lg p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-amber-500 dark:text-amber-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">Complétude</span>
          </div>
          <span className="text-sm font-bold text-gray-900 dark:text-white">{overall}%</span>
        </div>
        <div className="h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${progressColor} transition-all duration-500`}
            style={{ width: `${overall}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-gray-900 dark:text-white font-semibold">Complétude de votre flotte</h3>
              <p className="text-gray-500 dark:text-slate-400 text-sm">
                {completedCount}/{totalCount} sections complétées
              </p>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 text-sm"
          >
            Masquer
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-500 dark:text-slate-400 text-sm">Progression globale</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">{overall}%</span>
        </div>
        <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${progressColor} transition-all duration-500`}
            style={{ width: `${overall}%` }}
          />
        </div>
      </div>

      {/* Checklist */}
      <div className="px-4 pb-2">
        <div className="space-y-1">
          {sections.map((section) => {
            const Icon = SECTION_ICONS[section.key] || Circle;
            return (
              <div
                key={section.key}
                className="flex items-center gap-3 py-2 px-3 rounded-lg transition-colors text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer"
                onClick={() => onNavigate?.(section.key)}
              >
                {section.complete ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                )}
                <Icon className={`w-4 h-4 flex-shrink-0 ${section.complete ? 'text-green-600 dark:text-green-500' : ''}`} />
                <span className="flex-1 text-sm">
                  {section.label}
                </span>
                {section.percentage !== undefined && (
                  <span className={`text-xs ${section.complete ? 'text-green-500' : 'text-amber-500 dark:text-amber-400'}`}>
                    {section.percentage}%
                  </span>
                )}
                <ChevronRight className="w-4 h-4 text-gray-400 dark:text-slate-500" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick actions for incomplete items */}
      {incompleteSections.length > 0 && (
        <div className="p-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-3">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">Actions suggérées</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {incompleteSections.map((section) => (
              <button
                key={section.key}
                onClick={() => onNavigate?.(section.key)}
                className="px-3 py-1.5 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 rounded-lg text-sm text-gray-900 dark:text-white transition-colors"
              >
                Compléter: {section.label.split('(')[0].trim()}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CompletenessCard;
